import { useState } from 'react'
import { TrashIcon, PlusIcon, BanknotesIcon, CurrencyDollarIcon, ReceiptRefundIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { FormInput, FormSelect } from '@/components/ui/form'
import clsx from 'clsx'

// ─── Types ───────────────────────────────────────────────

interface Payment {
  id: number
  amount: number
  date: string | null
  company_name: string | null
  credit_memo_applied?: boolean
}

interface CreditMemo {
  id: number
  number: string
  amount: number
  date: string | null
  deletable?: boolean
}

interface FinanceData {
  received_cost: number
  received_cost_mismatch: boolean
  vendor_invoice_checked: boolean
  due_date_to_vendor: string | null
  sum_payments_to_vendor: number
  balance_to_vendor: number
  sum_vendor_credit_memo: number
  vendor_available_credit: number
  open_balance: number
  sum_payments_from_customer: number
  sum_customer_credit_memo: number
  payments_to_vendor: Payment[]
  payments_from_customer: Payment[]
  vendor_credit_memos: CreditMemo[]
  customer_credit_memos: CreditMemo[]
}

interface FinanceTabProps {
  finance: FinanceData
  orderId: number
  currencySymbol: string
  csrfToken: string
  paymentOptions: { id: number; name: string }[]
  onNotify: (msg: string) => void
  onSaveField: (name: string, value: string) => void
}

// ─── Helpers ─────────────────────────────────────────────

function fmt(n: number, sym: string) { return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
function toRailsDate(iso: string) { const [y, m, d] = iso.split('-'); return y && m && d ? `${m}/${d}/${y}` : iso }

// ─── Section Card ────────────────────────────────────────

const COLORS: Record<string, { border: string; bg: string; text: string }> = {
  violet: { border: 'border-violet-100 dark:border-violet-500/20', bg: 'bg-violet-50/50 dark:bg-violet-500/5', text: 'text-violet-600 dark:text-violet-400' },
  cyan: { border: 'border-cyan-100 dark:border-cyan-500/20', bg: 'bg-cyan-50/50 dark:bg-cyan-500/5', text: 'text-cyan-600 dark:text-cyan-400' },
  amber: { border: 'border-amber-100 dark:border-amber-500/20', bg: 'bg-amber-50/50 dark:bg-amber-500/5', text: 'text-amber-600 dark:text-amber-400' },
  emerald: { border: 'border-emerald-100 dark:border-emerald-500/20', bg: 'bg-emerald-50/50 dark:bg-emerald-500/5', text: 'text-emerald-600 dark:text-emerald-400' },
  rose: { border: 'border-rose-100 dark:border-rose-500/20', bg: 'bg-rose-50/50 dark:bg-rose-500/5', text: 'text-rose-600 dark:text-rose-400' },
  sky: { border: 'border-sky-100 dark:border-sky-500/20', bg: 'bg-sky-50/50 dark:bg-sky-500/5', text: 'text-sky-600 dark:text-sky-400' },
}

const SECTION_ICONS: Record<string, { icon: React.FC<React.SVGProps<SVGSVGElement>>; fg: string; bg: string }> = {
  'Vendor Finance': { icon: BanknotesIcon, fg: 'text-violet-700 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-500/10' },
  'Outgoing Payments': { icon: ArrowUpTrayIcon, fg: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10' },
  'Vendor Credit Memos': { icon: ReceiptRefundIcon, fg: 'text-rose-700 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-500/10' },
  'Customer Finance': { icon: CurrencyDollarIcon, fg: 'text-cyan-700 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-500/10' },
  'Incoming Payments': { icon: ArrowDownTrayIcon, fg: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
  'Customer Credit Memos': { icon: DocumentTextIcon, fg: 'text-sky-700 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-500/10' },
}

function FinanceSection({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  const c = COLORS[color] || COLORS.violet
  const iconDef = SECTION_ICONS[title]
  const Icon = iconDef?.icon
  return (
    <div className="mb-5 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <div className="p-4">
        <div className="flex items-center gap-3">
          {Icon && <span className={clsx('inline-flex shrink-0 rounded-md p-1.5', iconDef.bg, iconDef.fg)}><Icon className="h-4 w-4" /></span>}
          <h3 className={clsx('text-base font-semibold', c.text)}>{title}</h3>
        </div>
      </div>
      <div className="border-t border-zinc-100 px-4 py-3 dark:border-zinc-800">{children}</div>
    </div>
  )
}

function SummaryRow({ label, value, className, noBorder }: { label: string; value: string; className?: string; noBorder?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-2 ${noBorder ? '' : 'border-b border-zinc-100 dark:border-zinc-800'}`}>
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={clsx('text-xs font-semibold', className || 'text-zinc-900 dark:text-white')}>{value}</span>
    </div>
  )
}

// ─── Payments Table ──────────────────────────────────────

function PaymentsTable({ payments, kind, orderId, csrfToken, paymentOptions, sym, onNotify, onAdd, onDelete }: {
  payments: Payment[]; kind: string; orderId: number; csrfToken: string
  paymentOptions: { id: number; name: string }[]; sym: string
  onNotify: (msg: string) => void; onAdd: (p: Payment) => void; onDelete: (id: number) => void
}) {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState('')
  const [companyId, setCompanyId] = useState('')

  const addPayment = async () => {
    if (!amount || !companyId) return
    const dateVal = date ? toRailsDate(date) : ''
    const res = await fetch(`/customer_orders/${orderId}/payments.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ payment: { amount, date: dateVal, company_id: companyId, kind } }),
    })
    if (res.ok) {
      const data = await res.json()
      onAdd(data)
      setAmount(''); setDate(''); setCompanyId('')
      onNotify('Saved:Payment added')
    }
  }

  const deletePayment = async (id: number) => {
    if (!confirm('Delete this payment?')) return
    const res = await fetch(`/customer_orders/${orderId}/payments/${id}.json`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken },
    })
    if (res.ok) { onDelete(id); onNotify('Deleted:Payment removed') }
  }

  return (
    <div>
      {payments.length > 0 && (
        <table className="mb-2 w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-500 dark:border-zinc-700">
              <th className="py-1 pr-2">#</th>
              <th className="py-1 pr-2">Amount</th>
              <th className="py-1 pr-2">Date</th>
              <th className="py-1 pr-2">Company</th>
              <th className="py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p, i) => (
              <tr key={p.id} className="group border-b border-zinc-100 dark:border-zinc-800">
                <td className="py-1 pr-2 text-zinc-400">{i + 1}</td>
                <td className="py-1 pr-2 font-medium">{fmt(p.amount, sym)}</td>
                <td className="py-1 pr-2 text-zinc-500">{p.date || '—'}</td>
                <td className="py-1 pr-2 text-zinc-500">{p.company_name || '—'}</td>
                <td className="py-1">
                  <button onClick={() => deletePayment(p.id)}
                    className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700">
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center gap-2">
        <div className="w-24"><FormInput type="number" placeholder="Amount *" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div className="w-32"><FormInput type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="w-36"><FormSelect value={companyId} onChange={setCompanyId} placeholder="Company *"
          options={paymentOptions.map(c => ({ value: String(c.id), label: c.name }))} /></div>
        <button onClick={addPayment} disabled={!amount || !companyId}
          className="rounded-md bg-indigo-600 p-1.5 text-white hover:bg-indigo-500 disabled:opacity-40">
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Credit Memos Table ──────────────────────────────────

function CreditMemosTable({ memos, kind, orderId, csrfToken, sym, onNotify, onAdd, onDelete }: {
  memos: CreditMemo[]; kind: string; orderId: number; csrfToken: string; sym: string
  onNotify: (msg: string) => void; onAdd: (cm: CreditMemo) => void; onDelete: (id: number) => void
}) {
  const [number, setNumber] = useState('')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState('')

  const addMemo = async () => {
    if (!number || !amount) return
    const dateVal = date ? toRailsDate(date) : ''
    const res = await fetch(`/customer_orders/${orderId}/credit_memos.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ credit_memo: { number, amount, date: dateVal, kind } }),
    })
    if (res.ok) {
      const data = await res.json()
      onAdd(data)
      setNumber(''); setAmount(''); setDate('')
      onNotify('Saved:Credit memo added')
    }
  }

  const deleteMemo = async (id: number) => {
    if (!confirm('Delete this credit memo?')) return
    const res = await fetch(`/customer_orders/${orderId}/credit_memos/${id}.json`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken },
    })
    if (res.ok) { onDelete(id); onNotify('Deleted:Credit memo removed') }
  }

  return (
    <div>
      {memos.length > 0 && (
        <table className="mb-2 w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-500 dark:border-zinc-700">
              <th className="py-1 pr-2">#</th>
              <th className="py-1 pr-2">Number</th>
              <th className="py-1 pr-2">Amount</th>
              <th className="py-1 pr-2">Date</th>
              <th className="py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {memos.map((cm, i) => (
              <tr key={cm.id} className="group border-b border-zinc-100 dark:border-zinc-800">
                <td className="py-1 pr-2 text-zinc-400">{i + 1}</td>
                <td className="py-1 pr-2">{cm.number}</td>
                <td className="py-1 pr-2 font-medium">{fmt(cm.amount, sym)}</td>
                <td className="py-1 pr-2 text-zinc-500">{cm.date || '—'}</td>
                <td className="py-1">
                  {(cm.deletable !== false) && (
                    <button onClick={() => deleteMemo(cm.id)}
                      className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700">
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center gap-2">
        <div className="w-24"><FormInput type="text" placeholder="Number *" value={number} onChange={(e) => setNumber(e.target.value)} /></div>
        <div className="w-24"><FormInput type="number" placeholder="Amount *" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div className="w-32"><FormInput type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <button onClick={addMemo} disabled={!number || !amount}
          className="rounded-md bg-indigo-600 p-1.5 text-white hover:bg-indigo-500 disabled:opacity-40">
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────

export default function FinanceTab({ finance, orderId, currencySymbol: sym, csrfToken, paymentOptions, onNotify, onSaveField }: FinanceTabProps) {
  const [data, setData] = useState(finance)

  return (
    <div data-testid="finance-tab" className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
      {/* ── Left: Vendor ── */}
      <div>
        <FinanceSection title="Vendor Finance" color="violet">
          <SummaryRow label="Received Cost" value={fmt(data.received_cost, sym)}
            className={data.received_cost_mismatch ? 'text-red-500' : undefined} />
          <div className="flex items-center justify-between border-b border-zinc-100 py-2 dark:border-zinc-800">
            <span className="text-xs text-zinc-500">Invoice Checked?</span>
            <div className="w-20">
              <FormSelect value={String(data.vendor_invoice_checked)}
                onChange={(v) => { setData(prev => ({ ...prev, vendor_invoice_checked: v === 'true' })); onSaveField('vendor_invoice_checked', v) }}
                options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]} />
            </div>
          </div>
          <div className="flex items-center justify-between border-b border-zinc-100 py-2 dark:border-zinc-800">
            <span className="text-xs text-zinc-500">Due To Vendor</span>
            <div className="w-36">
              <FormInput type="date" value={data.due_date_to_vendor || ''} onChange={(e) => {
                setData(prev => ({ ...prev, due_date_to_vendor: e.target.value }))
                if (e.target.value) onSaveField('due_date_to_vendor', toRailsDate(e.target.value))
              }} />
            </div>
          </div>
          <SummaryRow label="Payments Out" value={fmt(data.sum_payments_to_vendor, sym)} />
          <SummaryRow label="Balance To Vendor" value={fmt(data.balance_to_vendor, sym)}
            className={data.balance_to_vendor > 0 ? 'text-amber-600' : 'text-emerald-600'} />
          <SummaryRow label="Vendor Credit Memo" value={fmt(data.sum_vendor_credit_memo, sym)} />
          <SummaryRow label="Available Credit" value={fmt(data.vendor_available_credit, sym)}
            className={data.vendor_available_credit > 0 ? 'text-emerald-600' : undefined} noBorder />
        </FinanceSection>

        <FinanceSection title="Outgoing Payments" color="amber">
          <PaymentsTable payments={data.payments_to_vendor} kind="to_vendor" orderId={orderId}
            csrfToken={csrfToken} paymentOptions={paymentOptions} sym={sym} onNotify={onNotify}
            onAdd={(p) => setData(prev => ({ ...prev, payments_to_vendor: [...prev.payments_to_vendor, p], sum_payments_to_vendor: prev.sum_payments_to_vendor + p.amount }))}
            onDelete={(id) => { const p = data.payments_to_vendor.find(x => x.id === id); setData(prev => ({ ...prev, payments_to_vendor: prev.payments_to_vendor.filter(x => x.id !== id), sum_payments_to_vendor: prev.sum_payments_to_vendor - (p?.amount || 0) })) }} />
        </FinanceSection>

        <FinanceSection title="Vendor Credit Memos" color="rose">
          <CreditMemosTable memos={data.vendor_credit_memos} kind="vendor" orderId={orderId}
            csrfToken={csrfToken} sym={sym} onNotify={onNotify}
            onAdd={(cm) => setData(prev => ({ ...prev, vendor_credit_memos: [...prev.vendor_credit_memos, cm], sum_vendor_credit_memo: prev.sum_vendor_credit_memo + cm.amount }))}
            onDelete={(id) => { const cm = data.vendor_credit_memos.find(x => x.id === id); setData(prev => ({ ...prev, vendor_credit_memos: prev.vendor_credit_memos.filter(x => x.id !== id), sum_vendor_credit_memo: prev.sum_vendor_credit_memo - (cm?.amount || 0) })) }} />
        </FinanceSection>
      </div>

      {/* ── Right: Customer ── */}
      <div>
        <FinanceSection title="Customer Finance" color="cyan">
          <SummaryRow label="Open Balance" value={fmt(data.open_balance, sym)}
            className={data.open_balance > 0 ? 'text-amber-600' : 'text-emerald-600'} />
          <SummaryRow label="Summary Payments" value={fmt(data.sum_payments_from_customer, sym)} />
          <SummaryRow label="Customer Credit Memo" value={fmt(data.sum_customer_credit_memo, sym)} noBorder />
        </FinanceSection>

        <FinanceSection title="Incoming Payments" color="emerald">
          <PaymentsTable payments={data.payments_from_customer} kind="from_customer" orderId={orderId}
            csrfToken={csrfToken} paymentOptions={paymentOptions} sym={sym} onNotify={onNotify}
            onAdd={(p) => setData(prev => ({ ...prev, payments_from_customer: [...prev.payments_from_customer, p], sum_payments_from_customer: prev.sum_payments_from_customer + p.amount }))}
            onDelete={(id) => { const p = data.payments_from_customer.find(x => x.id === id); setData(prev => ({ ...prev, payments_from_customer: prev.payments_from_customer.filter(x => x.id !== id), sum_payments_from_customer: prev.sum_payments_from_customer - (p?.amount || 0) })) }} />
        </FinanceSection>

        <FinanceSection title="Customer Credit Memos" color="sky">
          <CreditMemosTable memos={data.customer_credit_memos} kind="customer" orderId={orderId}
            csrfToken={csrfToken} sym={sym} onNotify={onNotify}
            onAdd={(cm) => setData(prev => ({ ...prev, customer_credit_memos: [...prev.customer_credit_memos, cm], sum_customer_credit_memo: prev.sum_customer_credit_memo + cm.amount }))}
            onDelete={(id) => { const cm = data.customer_credit_memos.find(x => x.id === id); setData(prev => ({ ...prev, customer_credit_memos: prev.customer_credit_memos.filter(x => x.id !== id), sum_customer_credit_memo: prev.sum_customer_credit_memo - (cm?.amount || 0) })) }} />
        </FinanceSection>
      </div>
    </div>
  )
}
